import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useToast } from "../components";
import { useGetPartnerOffering } from "../data/api";
import { getTranzilaAdditionalParams } from "../data/resource_integration";
import { buildPathWithParams, useQueryString } from "../hooks";
import { FirstDayOfWeek } from "../lib/datetime";
import { useI18n } from "../lib/i18n/i18n_provider";
import {
  useTrackCheckoutStartedEcommerceEventOnMount,
  useTrackOrderCompletedEcommerceEvent,
  useTrackProductViewedEcommerceEvent,
} from "./analytics_ecommerce_events";
import {
  Order,
  useLocationV2Query,
  useQuoteQuery,
  useSpaceOfferingsQuery,
  useSpaceV2Query,
  useTranzilaLocationPartnerIntegration,
} from "./api";
import { DailyAccessCheckoutBoxDesktop } from "./checkout_box_desktop";
import { DailyAccessCheckoutPageMobile } from "./checkout_box_mobile";
import {
  normalizeDailyAccessOrderParams,
  toQuoteInput,
  useDailyAccessOrderParamsHandlers,
  useSpaceAvailableDaysQuery,
} from "./order_params";
import { PartnerConfig } from "./partner_config";
import {
  TemplateOfferingCheckoutTranzilaBase,
  useCheckoutTranzila,
} from "./template_offering_checkout_tranzila_base";
import { usePrevious } from "../hooks/use_previous";

interface TemplateOfferingCheckoutTranzilaParams {
  offeringID: string;
}

export interface TemplateOfferingCheckoutTranzilaQueryParams {
  spaceID: string;
  startDate?: string;
  endDate?: string;
  addOnID?: string;
  spaceOfferingID?: string;
}

export interface TemplateOfferingCheckoutTranzilaDailyAccessProps {
  limitQuantityPicker?: number;
  showTalkWithUsPrompt?: boolean;
  firstDayOfWeek?: FirstDayOfWeek;
  defaultMarketingConsent?: boolean;
  partnerConfig: PartnerConfig;
}

export function TemplateOfferingCheckoutTranzilaDailyAccess(
  props: TemplateOfferingCheckoutTranzilaDailyAccessProps
) {
  const {
    partnerConfig,
    firstDayOfWeek,
    showTalkWithUsPrompt,
    defaultMarketingConsent,
    limitQuantityPicker,
  } = props;
  const {
    tranzilaId,
    favicon,
    termsURL,
    hubspotForm,
    showLanguageSelector = false,
  } = partnerConfig;
  const { fbt } = useI18n();
  const pageParams = useParams<TemplateOfferingCheckoutTranzilaParams>();
  const partnerOfferingID = pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const queryParams =
    useQueryString<TemplateOfferingCheckoutTranzilaQueryParams>();
  const toast = useToast();

  const additionalDayPassAddOn = useMemo(
    () => ({
      id: "daypass",
      name: fbt("Daily (1 pass)"),
      description: "",
      daysToExpire: 0,
      numberOfEntries: 1,
      productCategory: "",
      productTypeName: "",
      price: 0,
      spaceID: "",
      addOnID: "daypass",
    }),
    [fbt]
  );
  const whitelistedAddOns = useMemo(() => {
    const cloned = partnerOffering.addOns.slice();
    cloned.unshift(additionalDayPassAddOn);
    return cloned;
  }, [additionalDayPassAddOn, partnerOffering.addOns]);

  const includeQuantity = partnerConfig.includeQuantity || false;
  const {
    orderParams,
    orderParamsError,
    errorText,
    validateOrderParams,
    handleChangeLocation,
    handleChangeAddOnID,
    handleChangeDateRange,
    handleChangeQuantity,
    handleChangePromoCode,
    handleChangeEmail,
  } = useDailyAccessOrderParamsHandlers(whitelistedAddOns, {
    ...normalizeDailyAccessOrderParams(queryParams, includeQuantity),
    spaceID: queryParams.spaceID,
  });
  const [order, setOrder] = useState<Order>();
  const spaceID = queryParams.spaceID;
  const space = useSpaceV2Query(spaceID);
  const location = useLocationV2Query(space?.locationID);
  const tranzilaLocationPartnerIntegration =
    useTranzilaLocationPartnerIntegration(space?.locationID);
  const { isOutsideRange, isBlockedDate, handleNextVisibleDates } =
    useSpaceAvailableDaysQuery(spaceID);
  const { data: spaceOfferings } = useSpaceOfferingsQuery(
    spaceID,
    partnerOffering.addOns.map((addOn) => addOn.id)
  );
  const multiPasses = useMemo(() => {
    const cloned = spaceOfferings?.slice();
    cloned?.unshift(additionalDayPassAddOn);
    return cloned;
  }, [additionalDayPassAddOn, spaceOfferings]);
  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      spaceID && spaceLayoutID
        ? toQuoteInput(spaceID, partnerOffering.partner.id, orderParams, "WL", {
            layoutID: spaceLayoutID,
            attendees: 1,
          })
        : null,
    [spaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );

  const {
    data: quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuoteQuery(quoteInput);
  const history = useHistory();

  const offeringName =
    space?.productType && location
      ? fbt(
          fbt.param("productTypeName", space.productType?.name) +
            " to " +
            fbt.param("locationName", location.name)
        )
      : "";
  const spaceOffering = partnerOffering.addOns.find(
    (po) => po.addOnID === orderParams.addOnID
  );
  const trackProductViewed = useTrackProductViewedEcommerceEvent();
  const prevSpace = usePrevious(space);
  const prevSpaceOffering = usePrevious(spaceOffering);
  useEffect(() => {
    if (
      quote &&
      space &&
      (prevSpace?.id !== space.id ||
        spaceOffering?.id !== prevSpaceOffering?.id)
    ) {
      // We want to track item viewed event when space is changed on checkout page
      // so that "Product Viewed" is always greater than "Checkout Started" in funnel
      // and reflect the fact that they are viewing a new item
      trackProductViewed(
        offeringName,
        space,
        location,
        quote,
        spaceOffering,
        partnerOffering
      );
    }
  }, [
    offeringName,
    space,
    partnerOffering.offeringTitle,
    prevSpace,
    quote,
    trackProductViewed,
    location,
    spaceOffering,
    prevSpaceOffering,
    partnerOffering,
  ]);
  useTrackCheckoutStartedEcommerceEventOnMount(
    offeringName,
    quote,
    space,
    location,
    spaceOffering,
    partnerOffering
  );
  const trackOrderCompletedEcommerceEvent =
    useTrackOrderCompletedEcommerceEvent(partnerOffering);

  const handlePaymentSuccess = useCallback(() => {
    if (order) {
      trackOrderCompletedEcommerceEvent(
        offeringName,
        order,
        space,
        location,
        spaceOffering
      );

      const pathWithParams = buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      );
      history.push(pathWithParams);
    }
  }, [
    order,
    trackOrderCompletedEcommerceEvent,
    offeringName,
    space,
    location,
    partnerOffering.slug,
    history,
    spaceOffering,
  ]);

  const requirePayment = quote?.totalPrice !== 0;
  const askForTax = space?.bookingPolicy.askForTax && requirePayment;
  const form = useCheckoutTranzila({
    requireBillingDetails: requirePayment,
    requirePayment: requirePayment,
    askForTax,
    initialValues: {
      fullName: "",
      companyName: "",
      phoneNumber: "",
      email: "",
      billingStreetAddress: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      billingCountry: "",
      billingTax: "",
      termsChecked: false,
      marketingConsent: defaultMarketingConsent || false,
    },
    partnerID: partnerOffering.partner.id,
    partnerOfferingID: partnerOffering.id,
    legacy__details: {
      layoutID: spaceLayoutID || "",
      attendees: 1,
    },
    orderParams,
    onOrderSubmitted: (order) => {
      setOrder(order);
    },
    onError: (error) => {
      toast.error({
        message: error,
      });
    },
  });

  const disabledCheckout = !!(quoteLoading || quoteError);

  return (
    <TemplateOfferingCheckoutTranzilaBase
      disabledCheckout={disabledCheckout}
      showLanguageSelector={showLanguageSelector}
      localeOptions={partnerConfig.localeOptions}
      displayAccessibility={partnerConfig.displayAccessibility}
      showTalkWithUsPrompt={showTalkWithUsPrompt}
      offeringName={offeringName}
      partnerOfferingSlug={partnerOfferingID}
      onPaymentSuccess={handlePaymentSuccess}
      tranzilaId={tranzilaId}
      requirePayment={requirePayment}
      tranzilaAdditionalParams={getTranzilaAdditionalParams(
        tranzilaLocationPartnerIntegration
      )}
      order={order}
      onCloseTranzila={() => setOrder(undefined)}
      favicon={favicon}
      homepageURL={partnerOffering.partner.homepageUrl}
      termsURL={termsURL}
      hubspotForm={hubspotForm}
      location={location}
      space={space}
      partnerOffering={partnerOffering}
      askForTax={askForTax}
      orderParams={orderParams}
      orderParamsError={errorText}
      validateOrderParams={validateOrderParams}
      handleChangePromoCode={handleChangePromoCode}
      quote={quote}
      onPressBack={() => {
        if (history.length > 1) {
          history.goBack();
        } else {
          const pathWithParams = buildPathWithParams(
            `/offerings/${partnerOfferingID}`
          );
          history.replace(pathWithParams);
        }
      }}
      onChangeEmail={handleChangeEmail}
      mobileCheckoutBox={
        <DailyAccessCheckoutPageMobile
          includeQuantity={includeQuantity}
          limitQuantityPicker={limitQuantityPicker}
          quoteError={quoteError}
          firstDayOfWeek={firstDayOfWeek}
          orderParams={orderParams}
          orderParamsError={orderParamsError}
          multiPasses={multiPasses || []}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeLocation={handleChangeLocation}
          onChangeAddOnID={handleChangeAddOnID}
          onChangeDateRange={handleChangeDateRange}
          onChangeQuantity={handleChangeQuantity}
          onNextVisibleDates={handleNextVisibleDates}
          locations={partnerOffering.offeringLocations}
        />
      }
      desktopCheckoutBox={
        <DailyAccessCheckoutBoxDesktop
          includeQuantity={includeQuantity}
          limitQuantityPicker={limitQuantityPicker}
          quoteError={quoteError}
          quoteLoading={quoteLoading}
          firstDayOfWeek={firstDayOfWeek}
          orderParams={orderParams}
          multiPasses={multiPasses || []}
          orderParamsErrors={orderParamsError}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeAddOnID={handleChangeAddOnID}
          onChangeLocation={handleChangeLocation}
          onChangeDateRange={handleChangeDateRange}
          onChangeQuantity={handleChangeQuantity}
          onNextVisibleDates={handleNextVisibleDates}
          locations={partnerOffering.offeringLocations}
          location={location}
          quote={quote}
          space={space}
          showCTA={false}
        />
      }
      form={form}
    />
  );
}
